import { useEffect, useRef, useState } from 'react';

import { readLocal, writeLocal } from '../utils/safeStorage';

interface TruckRunnerGameProps {
  onClose: () => void;
}

/**
 * TruckRunnerGame Component
 *
 * A simple side-scrolling game inspired by Chrome's T-Rex game.
 * The STORT truck must jump over obstacles to score points.
 */
export default function TruckRunnerGame({ onClose }: TruckRunnerGameProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [gameState, setGameState] = useState<'idle' | 'playing' | 'gameOver'>('idle');
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const gameLoopRef = useRef<number>();
  const gameRef = useRef({
    truck: {
      x: 50,
      y: 0,
      width: 40,
      height: 30,
      velocityY: 0,
      isJumping: false,
    },
    obstacles: [] as Array<{ x: number; width: number; height: number; type: 'box' | 'cone' | 'tall_cone' }>,
    ground: 0,
    speed: 9,
    score: 0,
    frameCount: 0,
    nextObstacleFrame: 60,
  });

  // Load high score from localStorage
  useEffect(() => {
    const savedHighScore = readLocal('truckRunnerHighScore');
    if (savedHighScore) {
      setHighScore(parseInt(savedHighScore, 10));
    }
  }, []);

  // Game constants
  const GRAVITY = 0.65;
  const JUMP_FORCE = -10.5;
  const MIN_OBSTACLE_SPAWN_INTERVAL = 50;
  const MAX_OBSTACLE_SPAWN_INTERVAL = 80;
  const CANVAS_WIDTH = 800;
  const CANVAS_HEIGHT = 300;

  // Initialize game
  const initGame = () => {
    const game = gameRef.current;
    const ground = CANVAS_HEIGHT - 50;

    game.truck.y = ground - game.truck.height;
    game.truck.velocityY = 0;
    game.truck.isJumping = false;
    game.obstacles = [];
    game.ground = ground;
    game.speed = 9;
    game.score = 0;
    game.frameCount = 0;
    game.nextObstacleFrame = 60;

    setScore(0);
  };

  // Jump function
  const jump = () => {
    const game = gameRef.current;
    if (!game.truck.isJumping) {
      game.truck.velocityY = JUMP_FORCE;
      game.truck.isJumping = true;
    }
  };

  // Check collision
  const checkCollision = (
    truck: { x: number; y: number; width: number; height: number },
    obstacle: { x: number; width: number; height: number },
  ) => {
    const obstacleY = gameRef.current.ground - obstacle.height;

    return (
      truck.x < obstacle.x + obstacle.width &&
      truck.x + truck.width > obstacle.x &&
      truck.y < obstacleY + obstacle.height &&
      truck.y + truck.height > obstacleY
    );
  };

  // Game loop
  const gameLoop = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
return;
}

    const ctx = canvas.getContext('2d');
    if (!ctx) {
return;
}

    const game = gameRef.current;

    // Clear canvas
    ctx.fillStyle = '#F9FCFC';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Draw ground
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, game.ground);
    ctx.lineTo(CANVAS_WIDTH, game.ground);
    ctx.stroke();

    // Update truck physics
    game.truck.velocityY += GRAVITY;
    game.truck.y += game.truck.velocityY;

    // Ground collision
    if (game.truck.y >= game.ground - game.truck.height) {
      game.truck.y = game.ground - game.truck.height;
      game.truck.velocityY = 0;
      game.truck.isJumping = false;
    }

    // Draw truck (simplified red rectangle with wheels)
    ctx.fillStyle = '#c91227';
    ctx.fillRect(game.truck.x, game.truck.y, game.truck.width, game.truck.height);

    // Draw wheels
    ctx.fillStyle = '#374151';
    ctx.beginPath();
    ctx.arc(game.truck.x + 10, game.truck.y + game.truck.height, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(game.truck.x + 30, game.truck.y + game.truck.height, 5, 0, Math.PI * 2);
    ctx.fill();

    // Spawn obstacles with variable timing
    game.frameCount++;
    if (game.frameCount >= game.nextObstacleFrame) {
      const rand = Math.random();
      let type: 'box' | 'cone' | 'tall_cone';
      let height: number;

      if (rand < 0.33) {
        type = 'box';
        height = 35;
      } else if (rand < 0.66) {
        type = 'cone';
        height = 45;
      } else {
        type = 'tall_cone';
        height = 65;
      }

      game.obstacles.push({
        x: CANVAS_WIDTH,
        width: 25,
        height,
        type,
      });

      // Set next spawn interval randomly
      const nextInterval = Math.floor(
        Math.random() * (MAX_OBSTACLE_SPAWN_INTERVAL - MIN_OBSTACLE_SPAWN_INTERVAL) + MIN_OBSTACLE_SPAWN_INTERVAL,
      );
      game.nextObstacleFrame = game.frameCount + nextInterval;
    }

    // Update and draw obstacles
    for (let i = game.obstacles.length - 1; i >= 0; i--) {
      const obstacle = game.obstacles[i];
      obstacle.x -= game.speed;

      // Remove off-screen obstacles
      if (obstacle.x + obstacle.width < 0) {
        game.obstacles.splice(i, 1);
        game.score += 10;
        setScore(game.score);
        continue;
      }

      // Check collision
      if (checkCollision(game.truck, obstacle)) {
        setGameState('gameOver');
        if (game.score > highScore) {
          setHighScore(game.score);
          writeLocal('truckRunnerHighScore', game.score.toString());
        }
        return;
      }

      // Draw obstacle
      const obstacleY = game.ground - obstacle.height;

      if (obstacle.type === 'box') {
        ctx.fillStyle = '#9ca3af';
        ctx.fillRect(obstacle.x, obstacleY, obstacle.width, obstacle.height);
      } else if (obstacle.type === 'cone') {
        ctx.fillStyle = '#6b7280';
        // Draw cone shape
        ctx.beginPath();
        ctx.moveTo(obstacle.x + obstacle.width / 2, obstacleY);
        ctx.lineTo(obstacle.x, obstacleY + obstacle.height);
        ctx.lineTo(obstacle.x + obstacle.width, obstacleY + obstacle.height);
        ctx.closePath();
        ctx.fill();
      } else {
        // tall_cone
        ctx.fillStyle = '#4b5563';
        // Draw taller cone shape
        ctx.beginPath();
        ctx.moveTo(obstacle.x + obstacle.width / 2, obstacleY);
        ctx.lineTo(obstacle.x, obstacleY + obstacle.height);
        ctx.lineTo(obstacle.x + obstacle.width, obstacleY + obstacle.height);
        ctx.closePath();
        ctx.fill();
      }
    }

    // Draw score with background for better visibility
    ctx.font = 'bold 20px monospace';

    // Score background box
    const scoreText = `Score: ${game.score}m`;
    const highScoreText = `High: ${highScore}m`;
    const scoreWidth = Math.max(ctx.measureText(scoreText).width, ctx.measureText(highScoreText).width);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(CANVAS_WIDTH - scoreWidth - 30, 10, scoreWidth + 20, 60);

    // Score text
    ctx.fillStyle = '#1f2937';
    ctx.textAlign = 'right';
    ctx.fillText(scoreText, CANVAS_WIDTH - 20, 35);
    ctx.fillText(highScoreText, CANVAS_WIDTH - 20, 58);
    ctx.textAlign = 'left';

    // Increase speed even more aggressively
    if (game.frameCount % 120 === 0) {
      game.speed += 1.2;
    }

    gameLoopRef.current = requestAnimationFrame(gameLoop);
  };

  // Start game
  const startGame = () => {
    initGame();
    setGameState('playing');
  };

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (gameState === 'idle' || gameState === 'gameOver') {
          startGame();
        } else if (gameState === 'playing') {
          jump();
        }
      }
      if (e.code === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- jump and startGame are defined above but not memoized; gameState is the meaningful trigger
  }, [gameState, onClose]);

  // Handle touch/click for mobile
  const handleCanvasClick = () => {
    if (gameState === 'idle' || gameState === 'gameOver') {
      startGame();
    } else if (gameState === 'playing') {
      jump();
    }
  };

  // Start game loop when playing
  useEffect(() => {
    if (gameState === 'playing') {
      gameLoopRef.current = requestAnimationFrame(gameLoop);
    }

    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gameLoop is defined above and not memoized; gameState controls when to start/stop the loop
  }, [gameState, highScore]);

  // Draw idle/game over state
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
return;
}

    const ctx = canvas.getContext('2d');
    if (!ctx) {
return;
}

    if (gameState === 'idle' || gameState === 'gameOver') {
      // Clear canvas
      ctx.fillStyle = '#F9FCFC';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Draw ground
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, CANVAS_HEIGHT - 50);
      ctx.lineTo(CANVAS_WIDTH, CANVAS_HEIGHT - 50);
      ctx.stroke();

      // Draw truck
      const truckY = CANVAS_HEIGHT - 50 - 30;
      ctx.fillStyle = '#c91227';
      ctx.fillRect(50, truckY, 40, 30);

      // Draw wheels
      ctx.fillStyle = '#374151';
      ctx.beginPath();
      ctx.arc(60, truckY + 30, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(80, truckY + 30, 5, 0, Math.PI * 2);
      ctx.fill();

      // Draw text
      ctx.fillStyle = '#374151';
      ctx.font = '20px monospace';
      ctx.textAlign = 'center';

      if (gameState === 'idle') {
        ctx.fillText('Tryck mellanslag eller klicka för att starta', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 20);
        ctx.font = '14px monospace';
        ctx.fillStyle = '#6b7280';
        ctx.fillText('Hoppa över hinder för att samla poäng', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 10);
      } else {
        ctx.fillText(`GAME OVER - ${score}m`, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 30);
        ctx.font = '16px monospace';
        ctx.fillStyle = '#6b7280';
        ctx.fillText(`Highscore: ${highScore}m`, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
        ctx.fillText('Tryck mellanslag för att spela igen', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 30);
      }
    }
  }, [gameState, score, highScore]);

  return (
    <div className="w-full max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-section font-semibold text-gray-900">Truck Runner</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Stäng spel"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="relative w-full" style={{ paddingBottom: '37.5%' }}>
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          onClick={handleCanvasClick}
          className="absolute top-0 left-0 w-full h-full border-2 border-gray-200 rounded cursor-pointer"
        />
      </div>

      <p className="text-body text-gray-500 text-center mt-3">
        Desktop: Mellanslag för att hoppa, Mobil: Klicka på skärmen | ESC för att stänga
      </p>
    </div>
  );
}
